import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { SiteContextFacade } from '../../site-context/facade/site-context.facade';
import { CultureFacade } from '../../cultures/facade/cultures.facade';
import { CatalogsFacade } from '../../catalog/facade/catalogs.facade';
import { RoutingService } from '@boels/helpers';
import { CmsFacade } from '../../cms/facade/cms.facade';
import { Page, MetaDataModel } from '@boels/models';

export const CategoryMetaDataResolver: ResolveFn<{
  routeResolver: Page | undefined;
  metaData: MetaDataModel | undefined;
}> = (route: ActivatedRouteSnapshot) => {
  return resolve(route);
};

const resolve = (
  route: ActivatedRouteSnapshot,
): Observable<{
  routeResolver: Page | undefined;
  metaData: MetaDataModel | undefined;
}> => {
  const catalogsFacade = inject(CatalogsFacade);
  const cultureFacade = inject(CultureFacade);
  const siteContextFacade = inject(SiteContextFacade);
  const routingService = inject(RoutingService);
  const cmsFacadeService = inject(CmsFacade);
  const translocoService = inject(TranslocoService);

  return combineLatest([
    cmsFacadeService.activeLoaded$,
    cultureFacade.getActiveCulture(),
    siteContextFacade.getActiveBaseSiteEntity(),
    translocoService.selectTranslate(
      'storeDetails.metaDescription',
      {},
      { scope: 'storeFinder' },
    ),
  ]).pipe(
    filter(
      ([, activeCulture, activeBaseSite]) =>
        !!activeCulture && !!activeBaseSite?.uid,
    ),
    switchMap(
      ([page, activeCulture, activeBaseSite, metaDescriptionTranslated]) => {
        if (!activeBaseSite?.uid) {
          return of({ routeResolver: page, metaData: undefined });
        }

        return catalogsFacade
          .getCategoryForId(
            activeBaseSite.uid,
            activeCulture,
            route.params['categoryId'],
          )
          .pipe(
            map((category) => ({
              routeResolver: page,
              metaData: {
                metaTitle:
                  category?.metaTitle ||
                  (category?.name && category?.id !== '1'
                    ? category?.name + ' | Boels'
                    : ''),
                metaDescription:
                  category?.metaDescription || metaDescriptionTranslated,
              },
            })),
            take(1),
          );
      },
    ),
    tap((resolvedData) => {
      route.data = {
        ...route.data,
        resolvedData: resolvedData,
      };
      routingService.setActivatedRouteSnapshot(route);
    }),
    take(1),
  );
};
